/**
 * WalletOnboardingPage — citizen self-onboarding into NIPOST Pay.
 *
 * Live data:
 *   - POST /v1/wallet/wallets-mine          — idempotent citizen wallet
 *     get-or-create (service-financial-services). Requires Wallet/create-own.
 *   - POST /v1/kyc/tier-upgrade-requests    — NIN verification + wallet
 *     tier promotion (service-kyc). Backed by IdCentraNinAdapter when
 *     IDCENTRA_API_KEY is configured; otherwise the deterministic NIMC
 *     stub (test NIN that always passes: 90000000001).
 *   - POST /v1/wallet/liveness-checks       — biometric liveness (Phase 2 stub).
 */
import { useEffect, useState, type JSX } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Camera, CheckCircle, ChevronRight, Loader2, Shield, User, XCircle } from 'lucide-react';

import {
  createOwnWallet,
  type WalletResponse,
} from '../../lib/api/wallet';
import {
  getLivenessCheck,
  startLivenessCheck,
  type LivenessCheck,
} from '../../lib/api/liveness';
import {
  requestKycTierUpgrade,
  type TierUpgradeResponse,
} from '../../lib/api/nipost-pay';
import { useCustomerAuth } from '../../lib/auth/useCustomerAuth';
import {
  KycTierBadge,
  PageHeader,
  describeWalletError,
} from './NipostPayUI';

interface TierOption {
  readonly id: 'TIER_1' | 'TIER_2' | 'TIER_3';
  readonly label: string;
  readonly subtitle: string;
  readonly cap: string;
  readonly features: readonly string[];
}

const TIERS: readonly TierOption[] = [
  {
    id: 'TIER_1',
    label: 'Basic',
    subtitle: 'Phone + NIN',
    cap: '≤ ₦50,000 daily inflow',
    features: ['Airtime top-up', 'Bill payments', 'Basic P2P up to ₦10,000/day', 'Cash-in & cash-out'],
  },
  {
    id: 'TIER_2',
    label: 'Standard',
    subtitle: 'Phone + NIN + BVN',
    cap: '≤ ₦300,000 daily inflow',
    features: ['All Basic features', 'P2P up to ₦100,000/day', 'Interbank transfers', 'Virtual debit card'],
  },
  {
    id: 'TIER_3',
    label: 'Enhanced',
    subtitle: 'Phone + NIN + BVN + Government ID',
    cap: 'Unlimited inflow',
    features: ['All Standard features', 'Unlimited transactions', 'Physical debit card', 'Bulk disbursements'],
  },
];

export function WalletOnboardingPage(): JSX.Element {
  const { token } = useCustomerAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [nin, setNin] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [dob, setDob] = useState<string>('');
  const [ninVerified, setNinVerified] = useState<boolean>(false);
  const [selectedTier, setSelectedTier] = useState<TierOption['id']>('TIER_2');
  const [livenessId, setLivenessId] = useState<string | null>(null);
  const [livenessStatus, setLivenessStatus] = useState<LivenessCheck['status']>('PENDING');
  const [livenessScore, setLivenessScore] = useState<number | null>(null);
  const livenessDone = livenessStatus === 'PASSED';

  const startLiveness = useMutation<LivenessCheck, Error, undefined>({
    mutationFn: () => startLivenessCheck({ bearerToken: token ?? undefined }),
    onSuccess: (data) => {
      setLivenessId(data.id);
      setLivenessStatus(data.status);
      setLivenessScore(data.score);
    },
  });

  // Poll the liveness check until it transitions out of PENDING. The
  // test-mode adapter auto-passes after a 2 second delay; production
  // vendors fire a webhook + we'd reactively refetch then.
  useEffect(() => {
    if (!livenessId || livenessStatus !== 'PENDING') return;
    const handle = window.setInterval(() => {
      void getLivenessCheck(livenessId, { bearerToken: token ?? undefined }).then((row) => {
        setLivenessStatus(row.status);
        setLivenessScore(row.score);
        if (row.status !== 'PENDING') window.clearInterval(handle);
      });
    }, 1_000);
    return () => window.clearInterval(handle);
  }, [livenessId, livenessStatus, token]);

  const provision = useMutation<WalletResponse, Error, undefined>({
    mutationFn: () =>
      createOwnWallet({ bearerToken: token ?? undefined }),
    onSuccess: () => {
      // Small delay so the success card is briefly visible before navigating.
      setTimeout(() => {
        navigate('/nipost-pay');
      }, 1500);
    },
  });

  // Real NIN verification — calls service-kyc /v1/kyc/tier-upgrade-requests
  // which dispatches to the IDCentra adapter (or its NIMC stub fallback in
  // dev/e2e). On APPROVED / AUTO_APPROVED we mark the step complete.
  // REJECTED / NEEDS_REVIEW surface the backend reason verbatim.
  const verifyNinMutation = useMutation<TierUpgradeResponse, Error, string>({
    mutationFn: (ninValue: string) =>
      requestKycTierUpgrade({ nin: ninValue }, { bearerToken: token ?? undefined }),
    onSuccess: (data) => {
      // The backend's tier-upgrade-request also tries to promote the
      // wallet's tier — but during onboarding the wallet doesn't exist
      // yet, so promote returns NEEDS_REVIEW. From the citizen's POV the
      // NIN was successfully verified against the NIMC roll either way;
      // only an outright REJECTED should block the step.
      if (data.status === 'REJECTED') {
        setNinVerified(false);
      } else {
        setNinVerified(true);
      }
    },
    onError: () => {
      setNinVerified(false);
    },
  });

  function verifyNin(): void {
    if (nin.length !== 11) return;
    verifyNinMutation.mutate(nin);
  }

  function reset(): void {
    setStep(1);
    setNin('');
    setPhone('');
    setEmail('');
    setDob('');
    setNinVerified(false);
    setSelectedTier('TIER_2');
    setLivenessId(null);
    setLivenessStatus('PENDING');
    setLivenessScore(null);
    startLiveness.reset();
    provision.reset();
    verifyNinMutation.reset();
  }

  if (provision.isSuccess && provision.data) {
    return (
      <div>
        <PageHeader title="Wallet activated" description="Your NIPOST Pay wallet is ready to use." />
        <div className="max-w-md mx-auto bg-white rounded-xl border border-border p-8 text-center">
          <CheckCircle className="w-16 h-16 text-nipost-green mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Wallet activated</h2>
          <p className="text-muted text-sm mb-6">You can now top up, send money, and pay bills.</p>
          <div className="bg-gray-50 rounded-lg p-4 space-y-3 text-sm text-left">
            <div className="flex justify-between">
              <span className="text-muted">Wallet ID</span>
              <span className="font-mono font-semibold text-gray-900">
                {provision.data.walletNumber ?? provision.data.id.slice(0, 12)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted">KYC tier</span>
              <KycTierBadge tier={provision.data.kycTier ?? provision.data.tier ?? selectedTier} />
            </div>
          </div>
          <button
            type="button"
            onClick={reset}
            className="mt-6 px-4 py-2 text-sm font-medium text-white bg-nipost-green rounded-lg hover:bg-nipost-green-dark"
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Activate NIPOST Pay" description="Provision your wallet in three quick steps." />

      <div className="flex items-center justify-center gap-0 mb-8 max-w-md mx-auto">
        {[1, 2, 3].map((s, i) => (
          <div key={s} className="flex items-center flex-1">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0 ${
                step >= s ? 'bg-nipost-green text-white' : 'bg-gray-100 text-gray-400'
              }`}
            >
              {step > s ? <CheckCircle className="w-5 h-5" /> : s}
            </div>
            <div
              className={`flex-1 h-0.5 ${
                i < 2 ? (step > s ? 'bg-nipost-green' : 'bg-gray-200') : 'hidden'
              }`}
            />
          </div>
        ))}
      </div>
      <div className="flex justify-between max-w-md mx-auto -mt-6 mb-6 px-1">
        {(['Identity', 'KYC tier', 'Liveness'] as const).map((label, i) => (
          <span
            key={label}
            className={`text-xs font-medium ${step === i + 1 ? 'text-nipost-green' : 'text-muted'}`}
          >
            {label}
          </span>
        ))}
      </div>

      <div className="max-w-lg mx-auto bg-white rounded-xl border border-border p-6">
        {step === 1 ? (
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <User className="w-5 h-5 text-nipost-green" /> Identity
            </h3>
            <div>
              <label className="block text-sm text-muted mb-1" htmlFor="nin">NIN (11 digits)</label>
              <div className="flex gap-2">
                <input
                  id="nin"
                  value={nin}
                  onChange={(e) => {
                    setNin(e.target.value.replace(/\D/g, ''));
                    setNinVerified(false);
                    verifyNinMutation.reset();
                  }}
                  maxLength={11}
                  inputMode="numeric"
                  placeholder="12345678901"
                  className="flex-1 px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-nipost-green/20 focus:border-nipost-green"
                />
                <button
                  type="button"
                  onClick={verifyNin}
                  disabled={nin.length !== 11 || verifyNinMutation.isPending}
                  className="px-3 py-2 text-sm bg-nipost-green text-white rounded-lg font-medium hover:bg-nipost-green-dark disabled:opacity-50 inline-flex items-center gap-1.5"
                >
                  {verifyNinMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                  {verifyNinMutation.isPending ? 'Verifying' : 'Verify'}
                </button>
              </div>
              {ninVerified && verifyNinMutation.data ? (
                <p
                  className="text-xs text-nipost-green mt-1 flex items-center gap-1"
                  data-testid="nin-verified"
                >
                  <CheckCircle className="w-3 h-3" /> NIN verified
                  {verifyNinMutation.data.ninVerifiedName
                    ? ` — ${verifyNinMutation.data.ninVerifiedName}`
                    : ''}
                </p>
              ) : null}
              {verifyNinMutation.isError ? (
                <p
                  role="alert"
                  className="text-xs text-red-700 mt-1 flex items-start gap-1"
                  data-testid="nin-error"
                >
                  <XCircle className="w-3 h-3 flex-shrink-0 mt-0.5" />
                  {describeWalletError(verifyNinMutation.error)}
                </p>
              ) : verifyNinMutation.isSuccess &&
                verifyNinMutation.data.status === 'REJECTED' ? (
                <p
                  role="alert"
                  className="text-xs text-amber-700 mt-1 flex items-start gap-1"
                  data-testid="nin-rejected"
                >
                  <XCircle className="w-3 h-3 flex-shrink-0 mt-0.5" />
                  NIN rejected: {verifyNinMutation.data.reason ?? 'NIN could not be verified.'}
                </p>
              ) : null}
            </div>
            <div>
              <label className="block text-sm text-muted mb-1" htmlFor="phone">Phone number</label>
              <input
                id="phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+2348012345678"
                className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-nipost-green/20 focus:border-nipost-green"
              />
            </div>
            <div>
              <label className="block text-sm text-muted mb-1" htmlFor="email">Email (optional)</label>
              <input
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
                placeholder="citizen@example.com"
                className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-nipost-green/20 focus:border-nipost-green"
              />
            </div>
            <div>
              <label className="block text-sm text-muted mb-1" htmlFor="dob">Date of birth</label>
              <input
                id="dob"
                value={dob}
                onChange={(e) => setDob(e.target.value)}
                type="date"
                className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-nipost-green/20 focus:border-nipost-green"
              />
            </div>
            <button
              type="button"
              onClick={() => setStep(2)}
              disabled={!ninVerified || phone.length === 0}
              className="w-full px-4 py-2.5 text-sm font-medium text-white bg-nipost-green rounded-lg hover:bg-nipost-green-dark disabled:opacity-50 inline-flex items-center justify-center gap-2"
            >
              Continue <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        ) : null}

        {step === 2 ? (
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <Shield className="w-5 h-5 text-nipost-green" /> Pick a KYC tier
            </h3>
            <div className="space-y-3">
              {TIERS.map((t) => (
                <label
                  key={t.id}
                  className={`block border-2 rounded-xl p-4 cursor-pointer transition-colors ${
                    selectedTier === t.id ? 'border-nipost-green bg-green-50' : 'border-border hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="radio"
                      name="tier"
                      value={t.id}
                      checked={selectedTier === t.id}
                      onChange={() => setSelectedTier(t.id)}
                      className="mt-1 accent-nipost-green"
                    />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-gray-900">{t.label}</span>
                        <span className="text-xs font-medium text-nipost-green bg-nipost-green/10 px-2 py-0.5 rounded-full">
                          {t.cap}
                        </span>
                      </div>
                      <p className="text-xs text-muted mt-0.5 mb-2">{t.subtitle}</p>
                      <ul className="space-y-1">
                        {t.features.map((f) => (
                          <li key={f} className="text-xs text-gray-600 flex items-center gap-1.5">
                            <CheckCircle className="w-3 h-3 text-nipost-green flex-shrink-0" /> {f}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </label>
              ))}
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 border border-border rounded-lg hover:bg-gray-50"
              >
                Back
              </button>
              <button
                type="button"
                onClick={() => setStep(3)}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-nipost-green rounded-lg hover:bg-nipost-green-dark"
              >
                Continue
              </button>
            </div>
          </div>
        ) : null}

        {step === 3 ? (
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <Camera className="w-5 h-5 text-nipost-green" /> Liveness check
            </h3>
            <div className="border-2 border-dashed border-gray-300 rounded-xl p-10 text-center bg-gray-50">
              <Camera className="w-16 h-16 text-gray-400 mx-auto mb-3" />
              {livenessStatus === 'PASSED' ? (
                <div data-testid="liveness-passed">
                  <CheckCircle className="w-10 h-10 text-nipost-green mx-auto mb-2" />
                  <p className="font-semibold text-nipost-green">
                    Liveness passed (score {livenessScore ?? '—'}/100)
                  </p>
                </div>
              ) : livenessStatus === 'FAILED' ? (
                <p className="font-semibold text-red-600">Liveness failed — please retry.</p>
              ) : livenessId ? (
                <p className="text-sm text-muted inline-flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Verifying with biometric vendor…
                </p>
              ) : (
                <p className="text-sm text-muted">
                  Position your face within the frame and look directly at the camera.
                </p>
              )}
            </div>
            {!livenessId ? (
              <button
                type="button"
                onClick={() => startLiveness.mutate(undefined)}
                disabled={startLiveness.isPending}
                className="w-full px-4 py-2.5 text-sm font-medium text-white bg-nipost-green rounded-lg hover:bg-nipost-green-dark disabled:opacity-50 inline-flex items-center justify-center gap-2"
              >
                {startLiveness.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                Start liveness capture
              </button>
            ) : null}
            {startLiveness.isError ? (
              <div role="alert" className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">
                Could not start liveness — {startLiveness.error.message}
              </div>
            ) : null}

            {provision.isError ? (
              <div role="alert" className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
                <p className="font-medium">Wallet activation failed</p>
                <p className="mt-1 text-xs">{describeWalletError(provision.error)}</p>
              </div>
            ) : null}

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setStep(2)}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 border border-border rounded-lg hover:bg-gray-50"
              >
                Back
              </button>
              <button
                type="button"
                onClick={() => provision.mutate(undefined)}
                disabled={!livenessDone || provision.isPending}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-nipost-green rounded-lg hover:bg-nipost-green-dark disabled:opacity-50 inline-flex items-center justify-center gap-2"
              >
                {provision.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Activate wallet
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
